import { apiRequest } from '../../../shared/services/apiClient';
import type {
  TokenUsageBreakdownItem,
  TokenUsageGranularity,
  TokenUsageReport,
  TokenUsageReportQuery,
  TokenUsageSeriesPoint,
  TokenUsageTotals,
} from '../types/tokenUsage';

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toText = (value: unknown): string => (typeof value === 'string' ? value : '');

const normalizeGranularity = (value: unknown): TokenUsageGranularity => (
  value === 'week' || value === 'month' ? value : 'day'
);

const normalizeTotals = (payload: any): TokenUsageTotals => ({
  inputTokens: toCount(payload?.inputTokens),
  outputTokens: toCount(payload?.outputTokens),
  totalTokens: toCount(payload?.totalTokens),
  completionCount: toCount(payload?.completionCount),
  operationCount: toCount(payload?.operationCount),
  conversationCount: toCount(payload?.conversationCount),
  runCount: toCount(payload?.runCount),
});

const normalizeSeriesPoint = (payload: any): TokenUsageSeriesPoint => ({
  key: toText(payload?.key),
  fromLocal: toText(payload?.fromLocal),
  toLocal: toText(payload?.toLocal),
  inputTokens: toCount(payload?.inputTokens),
  outputTokens: toCount(payload?.outputTokens),
  totalTokens: toCount(payload?.totalTokens),
  completionCount: toCount(payload?.completionCount),
  operationCount: toCount(payload?.operationCount),
});

const normalizeBreakdownItem = (payload: any): TokenUsageBreakdownItem => ({
  label: toText(payload?.label),
  inputTokens: toCount(payload?.inputTokens),
  outputTokens: toCount(payload?.outputTokens),
  totalTokens: toCount(payload?.totalTokens),
  completionCount: toCount(payload?.completionCount),
  operationCount: toCount(payload?.operationCount),
});

const normalizeBreakdownList = (payload: unknown): TokenUsageBreakdownItem[] => (
  Array.isArray(payload) ? payload.map(normalizeBreakdownItem) : []
);

const appendFilter = (query: URLSearchParams, key: string, value?: string) => {
  const normalized = value?.trim();
  if (normalized) {
    query.set(key, normalized);
  }
};

export const getTokenUsageReport = async (
  options: TokenUsageReportQuery = {},
): Promise<TokenUsageReport> => {
  const query = new URLSearchParams();

  appendFilter(query, 'granularity', options.granularity);
  appendFilter(query, 'from', options.from);
  appendFilter(query, 'to', options.to);
  appendFilter(query, 'modelName', options.modelName);
  appendFilter(query, 'triggeredBy', options.triggeredBy);
  appendFilter(query, 'plugin', options.plugin);
  appendFilter(query, 'functionName', options.functionName);
  appendFilter(query, 'status', options.status);
  appendFilter(query, 'conversationId', options.conversationId);
  appendFilter(query, 'employeeId', options.employeeId);
  appendFilter(query, 'runId', options.runId);

  const suffix = query.toString();
  const payload = await apiRequest<any>(`/api/v1/ai/token-usage/report${suffix ? `?${suffix}` : ''}`);

  return {
    window: {
      generatedAtUtc: toText(payload?.window?.generatedAtUtc),
      timeZone: toText(payload?.window?.timeZone),
      granularity: normalizeGranularity(payload?.window?.granularity),
      fromLocal: toText(payload?.window?.fromLocal),
      toLocal: toText(payload?.window?.toLocal),
      fromUtc: toText(payload?.window?.fromUtc),
      toUtc: toText(payload?.window?.toUtc),
    },
    totals: normalizeTotals(payload?.totals),
    series: Array.isArray(payload?.series) ? payload.series.map(normalizeSeriesPoint) : [],
    breakdowns: {
      byModel: normalizeBreakdownList(payload?.breakdowns?.byModel),
      byTriggeredBy: normalizeBreakdownList(payload?.breakdowns?.byTriggeredBy),
      byPlugin: normalizeBreakdownList(payload?.breakdowns?.byPlugin),
    },
  };
};
